import {
  Box,
  Divider,
  Flex,
  Link,
  Price,
  PrimaryButton,
  Spinner,
  Text,
} from "flicket-ui";
import { useAtom } from "jotai";
import flatten from "lodash/flatten";
import isEmpty from "lodash/isEmpty";
import startCase from "lodash/startCase";
import { useRouter } from "next/router";
import { FC, ReactNode, useContext, useEffect, useState } from "react";
import useSWR from "swr";
import { ContentModal } from "~components";
import { Icon, SlateRenderer } from "~components/common";
import { getAfterPayLocaleByCurrency } from "~config";
import { BaseContext } from "~context";
import {
  BNPLIntegration,
  isBNPLIntegrationType,
} from "~features/checkout/checkout.types";
import useActiveReferralCampaign from "~features/referrals/useActiveReferralCampaign";
import BuyNowPayLater from "~features/seated-reservation/components/common/common.BuyNowPayLater";
import {
  IntegrationType,
  LandingPage,
  ReferralRewardType,
  ReleaseType,
  TaxCalculationMethod,
  TicketTypeKind,
} from "~graphql/sdk";
import { MembershipsSoldForUserDocument } from "~graphql/typed-document-nodes";
import {
  CreateOrderFn,
  useAccount,
  useIsMobile,
  useNonSeatedMultibuy,
  useOrganization,
  useReferral,
} from "~hooks";
import {
  googleAnalyticsMembershipAtom,
  googleAnalyticsReleaseAtom,
  trackAddToCartV4,
  trackRemoveFromCartV4,
  trackSelectItemV4,
  trackViewItemListV4,
} from "~hooks/useGoogleAnalytics";
import { useQuery } from "~hooks/useQuery";
import {
  calculateMultibuyDiscount,
  calculateTotal,
  OrganizationFeatures,
  sdk,
  showToast,
  trackAddToCart,
  trackRemoveFromCart,
  trackSelectItem,
  trackViewItemList,
} from "~lib";
import { isAvailableForSelection, isVisible } from "./non-seated.helpers";
import { ListItem } from "./non-seated.ListItem";
import useCaptureClick from "~telemetry/traces/useCaptureClick";
import { AttributeNames } from "~telemetry/enums/AttributeNames";
import styled from "styled-components";
import {
  INormalisedData,
  IReleaseTicket,
  TicketTypeType,
  IReleaseZone,
} from "~features/nonseated-reservation/nonseated-reservation.types";

const hasImportantNotice = (importantNotice?: string) =>
  importantNotice &&
  (JSON.parse(importantNotice) as any[]).some(({ children }) =>
    children.some(({ text }) => !isEmpty(text))
  );

type NonSeatedProps = {
  data: INormalisedData;
  zones: IReleaseZone[];
  addons: IReleaseTicket[];

  createOrder: CreateOrderFn;
  isMembership: boolean;
  releaseType?: ReleaseType;
  releaseNotes?: string;
  isDigital?: boolean;
  isResaleRelease?: boolean;
  allowPublicComp?: boolean;
  requiresAuth: boolean;
};

const StyledLink = styled.a`
  text-decoration: underline;
`;

type SelectedItem = IReleaseTicket & {
  quantity: number;
};

export const ReservationList: FC<NonSeatedProps> = ({
  data,
  addons: availableAddons = [],
  zones = [],
  createOrder,
  isMembership,
  releaseNotes,
  isDigital,
  isResaleRelease,
  releaseType,
  allowPublicComp,
  requiresAuth,
}) => {
  const { activeReferralCampaign } = useActiveReferralCampaign(data.id);
  const router = useRouter();
  const { isAdmin, isPOS, isLoggedIn, user } = useAccount();
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [delayedIsSubmitting, setDelayedSubmitting] = useState(false);
  const { organization, isSinglePage } = useContext(BaseContext);
  const { hasFeature } = useOrganization();
  const [importantNotice, setImportantNotice] = useState(releaseNotes);
  const [alertError, setAlertError] = useState<
    { title: string; message: string | ReactNode } | undefined
  >();
  const ticketVariant = isDigital ? "pass code" : "ticket";
  const isMobile = useIsMobile();
  const { calculateReferralDiscount } = useReferral();

  const selectedTickets = selectedItems.filter((i) => i.type !== "addon");
  const selectedAddons = selectedItems.filter((i) => i.type === "addon");
  const userCredits = user?.credits ?? 0;

  const bnplGateways = data?.gateways?.filter(
    (gateway) => !!gateway && isBNPLIntegrationType(gateway.type)
  );

  const stripeAfterpayGateway = data?.gateways?.find(
    (gateway) =>
      gateway.type === IntegrationType.PaymentStripe && gateway.bnplEnabled
  );

  const showHiddenFees = !!organization.features?.includes(
    OrganizationFeatures.ShowHiddenFees
  );

  useEffect(() => {
    let timer: NodeJS.Timeout = undefined;
    if (isSubmitting) {
      timer = setTimeout(() => {
        setDelayedSubmitting(true);
      }, 2000);
    } else {
      setDelayedSubmitting(false);
    }
    return () => timer && clearTimeout(timer);
  }, [isSubmitting]);

  useEffect(() => {
    releaseNotes
      ? setImportantNotice(releaseNotes)
      : setImportantNotice(data?.importantNotice);
  }, [releaseNotes]);

  const [release] = useAtom(googleAnalyticsReleaseAtom);
  const [membership] = useAtom(googleAnalyticsMembershipAtom);

  const { data: ticketsSoldForUserData } = useSWR(
    isLoggedIn && data?.id && !isMembership
      ? ["tickets-sold-for-user", organization.id, data?.id]
      : null,
    async (_: string, orgId: string, eventId: string) =>
      sdk({ orgId })
        .ticketsSoldForUser({ eventId })
        .then(({ ticketsSoldForUser }) => ticketsSoldForUser)
  );

  const { data: membershipsSoldForUserData } = useQuery(
    isLoggedIn && data?.id && isMembership && MembershipsSoldForUserDocument,
    {
      membershipId: data?.id,
    }
  );

  const captureClick = useCaptureClick({
    eventId: data?.id,
    forwardToNest: true,
  });

  const membershipsSoldForUser =
    membershipsSoldForUserData?.membershipsSoldForUser;

  const ticketsSoldForUser =
    ticketsSoldForUserData ?? membershipsSoldForUser ?? [];

  /** @note because GA multibuy and non GA multibuy differ too much we have a new hook */
  const { activePromotions } = useNonSeatedMultibuy(
    data?.multiBuyPromotions,
    selectedTickets.map((s, key) => ({
      ...s,
      id: `${s.id}-${key}`,
      [isMembership ? "membershipType" : "ticketType"]: { id: s.id },
    }))
  );

  const allTicketTypes = flatten(zones.map((zone) => zone.ticketTypes)).filter(
    (ticketType) => {
      if (
        ticketType.kind !== TicketTypeKind.Standard &&
        !(isAdmin || allowPublicComp)
      ) {
        return false;
      }

      return ticketType.isActive;
    }
  );

  const addonTypes = availableAddons?.filter(
    (addon) =>
      isAdmin ||
      addon.isActive ||
      (release && release.type !== ReleaseType.Listed)
  );

  const allTicketTypesAndAddons = [...addonTypes, ...allTicketTypes];
  const selectedTicketTypesAndAddons = [...selectedTickets, ...selectedAddons];

  const multibuyDiscount = selectedTickets?.every(
    (seat) => seat.zoneId === selectedTickets?.[0]?.zoneId
  )
    ? calculateMultibuyDiscount(
        activePromotions[0],
        allTicketTypesAndAddons,
        zones?.find((zone) => zone.name === selectedTickets?.[0]?.zoneId)
          ?.ticketTypes
      )
    : 0;

  const onChange = (
    id: string,
    value: number,
    type: TicketTypeType = "ticket",
    zoneId: string,
    price: number
  ) => {
    const current = allTicketTypesAndAddons?.find(
      (ticketType) => ticketType.id === id
    );

    const itemExists = selectedItems.find(
      (item) => item.id === id && item.zoneId === zoneId
    );

    let updatedItem = {
      ...itemExists,
      quantity: value,
      zoneId,
    };

    //if the item doesnt exist then add the details from current to it
    if (!itemExists) {
      updatedItem = {
        ...updatedItem,
        name: current.name,
        id: current.id,
      };
    }

    const trackItem = {
      ...updatedItem,
      price,
    };

    trackSelectItem(trackItem);
    trackSelectItemV4({
      item: trackItem,
      eventOrMembershipId: data.id,
      membership,
      release,
    });

    let itemsToAdd: SelectedItem[] = [];

    if (selectedItems.length === 0) {
      trackAddToCart(trackItem);
      trackAddToCartV4(organization, {
        eventOrMembershipId: data.id,
        item: trackItem,
        membership,
        release,
      });
      itemsToAdd = [{ ...current, quantity: value, zoneId, type }];
    } else if (!itemExists) {
      trackAddToCart(trackItem);
      trackAddToCartV4(organization, {
        eventOrMembershipId: data.id,
        item: trackItem,
        membership,
        release,
      });
      itemsToAdd = [
        ...selectedItems,
        { ...current, quantity: value, zoneId, type },
      ];
    } else {
      const existingSelectedItem = selectedItems?.filter(
        (item) => item.id === id && item.zoneId === zoneId
      );
      const change = value - existingSelectedItem[0].quantity;

      trackItem.quantity = change < 0 ? change * -1 : change;

      itemsToAdd = selectedItems?.filter(
        (item) => item.id !== id || item.zoneId !== zoneId
      );

      // only if value bigger than 0 we update the item
      if (value > 0) {
        if (change > 0) {
          trackAddToCart(trackItem);
          trackAddToCartV4(organization, {
            eventOrMembershipId: data.id,
            item: trackItem,
            membership,
            release,
          });
        } else if (change < 0) {
          trackRemoveFromCart(trackItem);
          trackRemoveFromCartV4(organization, {
            eventOrMembershipId: data.id,
            item: trackItem,
            membership,
            release,
          });
        }

        itemsToAdd = [...itemsToAdd, updatedItem];
      } else {
        // otherwise we remove the item
        trackRemoveFromCart(trackItem);
        trackRemoveFromCartV4(organization, {
          eventOrMembershipId: data.id,
          item: trackItem,
          membership,
          release,
        });
      }
    }

    if (!isAdmin) {
      // Remove any selected entities that no longer meet the purchase restriction
      for (const selectedItem of itemsToAdd) {
        if (
          !isAvailableForSelection(
            itemsToAdd,
            selectedItem,
            ticketsSoldForUser
          ) ||
          selectedItem.quantity <= 0
        ) {
          const index = itemsToAdd.findIndex(
            (item) => item.id === selectedItem.id
          );

          itemsToAdd.splice(index, 1);
        }
      }

      // Display error modal if purchase restrictions fail
      if (value > 0) {
        let success = itemsToAdd.some((item) => item.id === id);

        // Check what user has previously purchased
        if (!success) {
          success = ticketsSoldForUser.some((item) =>
            [item.ticketType?.id, item.membershipType?.id].includes(id)
          );
        }

        if (!success && current?.purchaseRestrictionsHelpText) {
          const purchaseRestrictions = current.purchaseRestrictionsHelpText;

          if (purchaseRestrictions.length > 0) {
            const message = (
              <>
                <Text>{current.name} must be purchased with: </Text>
                <ul>
                  {purchaseRestrictions.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
                {!user && (
                  <Text mt={4}>
                    Already purchased these?
                    <Link
                      ml={"1/2"}
                      textDecoration="underline"
                      cursor="pointer"
                      href={`/login?redirect=${encodeURIComponent(
                        `${router.asPath}?ticket=${current.id}&quantity=${value}`
                      )}`}
                    >
                      Log in
                    </Link>{" "}
                    to buy additional tickets.
                  </Text>
                )}
              </>
            );

            setAlertError({
              title: "This ticket has purchase restrictions",
              message,
            });
          }
        }
      }
    }
    setSelectedItems(itemsToAdd);
  };

  useEffect(() => {
    if (router.query.ticket && allTicketTypes?.length > 0) {
      // If we have a ticket id in the query this will scroll the page to
      // the ticket listing
      const element = document.getElementById(router.query.ticket as string);
      element?.scrollIntoView({ behavior: "smooth" });
    }
  }, [router.query.ticket, allTicketTypes?.length]);

  const handleOrderCreation = async () => {
    setIsSubmitting(true);
    if (selectedTickets.length === 0 && selectedAddons.length === 0) {
      showToast(
        `No ${isMembership ? "memberships" : ticketVariant} selected!`,
        "error"
      );
      return setIsSubmitting(false);
    }

    await createOrder(
      selectedTickets?.filter((seat) => {
        return seat.quantity > 0;
      }),
      selectedAddons,
      activePromotions?.[0]?.promotion?.id,
      (errorMessage) => {
        setAlertError({
          title: "There was a problem...",
          message: errorMessage,
        });

        return setIsSubmitting(false);
      }
    );
  };

  useEffect(() => {
    if (zones?.length > 0) {
      trackViewItemList(zones, allTicketTypes);
      trackViewItemListV4(data, {
        zones,
        ticketTypes: allTicketTypes,
        seats: selectedTickets,
        ticketsSoldForUser,
        isAdmin,
        addonTypes,
        release,
      });
    }
  }, [zones.length]);

  const hasTicketSelected =
    selectedTickets?.length > 0 || selectedAddons?.length > 0;

  const totalOptions = {
    showHiddenFees,
    fee: data?.bookingFee,
    type: data?.bookingFeeType,
  };

  const subTotal = calculateTotal(selectedTicketTypesAndAddons, totalOptions);
  const lineItemsTotal = calculateTotal(selectedTicketTypesAndAddons);

  const referralDiscount = calculateReferralDiscount(
    activeReferralCampaign,
    lineItemsTotal - multibuyDiscount
  );

  let totalPrice = subTotal - multibuyDiscount - referralDiscount;

  const taxRate = data.taxRate;
  const customTaxRate = data.customTaxRate;

  // note if taxes are inclusive then the total does not need taxes to be applied.
  const calculatedTaxes =
    data.taxCalculationMethod === TaxCalculationMethod.Exclusive && taxRate > 0
      ? (lineItemsTotal - multibuyDiscount - referralDiscount) * taxRate
      : 0;
  // note custom taxes are applied when the value is set at the org level on line items.
  const calculatedCustomTaxes =
    customTaxRate > 0 ? lineItemsTotal * customTaxRate : 0;

  totalPrice = totalPrice + calculatedTaxes + calculatedCustomTaxes;

  const showIsR18 = data.isR18;

  // this value has derived from the zone ticketTypes which uses a ResolvedField see backend for more info.
  const soldOutZones = zones?.filter((zone) =>
    zone.ticketTypes
      ?.filter((tt) => tt.isActive)
      ?.every((tt) => tt.isSoldOut ?? false)
  );

  const SoldOutWaitlistMessage = ({ zoneName }: { zoneName: string }) => {
    const ticketWaitlistLink = `/events/${data.id}/ticket-waitlist${
      release ? `?release=${release.id}` : ""
    }`;

    return (
      <Flex justifyContent={"center"}>
        <Text variant="extraBold.M">
          {zoneName} sold out –{" "}
          <StyledLink
            onClick={captureClick(
              (e) => {
                e.preventDefault();
                void router.push(ticketWaitlistLink);
              },
              "ticketing-page.join-waitlist",
              {
                [AttributeNames.FLICKET_ZONE_NAME]: zoneName,
              }
            )}
            href={ticketWaitlistLink}
          >
            join the waitlist
          </StyledLink>{" "}
          for ticket updates
        </Text>
      </Flex>
    );
  };

  const hasResaleWaitlist = data.ticketResaleWaitlistEnabled ?? false;

  const actionButton = () => {
    let actionButtonString = "";
    actionButtonString = actionButtonString.concat(
      actionButtonString,
      lineItemsTotal - multibuyDiscount === 0 ? "Get" : "Buy",
      " ",
      organization.landingPage === LandingPage.SingleEvent
        ? "ticket"
        : isMembership
        ? "membership"
        : ticketVariant,
      "s"
    );

    return actionButtonString;
  };

  if (delayedIsSubmitting) {
    return (
      <Box maxW={{ _: "100%", md: "640px" }} m={{ _: 0, md: "0 auto" as any }}>
        <Box my={20}>
          <Box mx={"1/2"} my={20} px={4}>
            <Spinner size={64} />
            <Text
              mt={4}
              fontSize={4}
              lineHeight="high"
              color="N600"
              textAlign="center"
            >
              We&apos;re just double-checking that you&apos;re not a
              ticket-buying robot in disguise.
            </Text>
            <Text
              mt={1}
              fontSize={4}
              lineHeight="high"
              color="N600"
              textAlign="center"
            >
              {" "}
              Your tickets are nearly ready.
            </Text>
            <Text
              textAlign="center"
              mt={4}
              fontSize={4}
              lineHeight="high"
              color="N600"
              fontWeight={"heavy"}
            >
              Don&apos;t refresh this page.
            </Text>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box maxW={{ _: "100%", md: "640px" }} m={{ _: 0, md: "0 auto" as any }}>
      {hasImportantNotice(importantNotice) && !isPOS && (
        <Box
          w="100%"
          p={2}
          bg="N100"
          borderRadius="sm"
          border="1px"
          borderColor="N300"
          mb={showIsR18 ? 2 : 5}
        >
          <Flex mb={3}>
            <Icon icon="info" color="N600" fontSize={6} />
            <Text
              fontSize={6}
              lineHeight="normal"
              fontWeight="heavy"
              color="N800"
              ml={1}
            >
              Important notes
            </Text>
          </Flex>
          <SlateRenderer value={importantNotice} />
        </Box>
      )}
      {showIsR18 && (
        <Flex
          alignItems="center"
          borderRadius="sm"
          backgroundColor="error25"
          px="6/4"
          py={2}
          mb={5}
        >
          <Icon icon="r18" fontSize={6} mr={1} />
          <Text
            fontWeight="medium"
            fontSize={2}
            lineHeight="medium"
            color="N700"
          >
            This is an R18 event
          </Text>
        </Flex>
      )}
      <Text
        color="N800"
        fontWeight="heavy"
        fontSize={{ _: 6, md: 7 }}
        textAlign={{ _: "center", md: "center" }}
        lineHeight={{ _: "normal", md: "high" }}
        marginTop={{ _: 3, md: 0 }}
        mb={2}
      >
        Select{" "}
        {isSinglePage
          ? `${startCase(ticketVariant)}s`
          : isMembership
          ? "Memberships"
          : `${startCase(ticketVariant)}s`}
      </Text>

      {zones?.map((zone) => {
        if (!zone.isActive) {
          return null;
        }

        const ticketListItems = zone.ticketTypes
          .filter((ticketType) => {
            if (!ticketType.isActive && releaseType !== ReleaseType.Presale) {
              return false;
            }

            if (
              !isVisible(selectedTickets, ticketType, ticketsSoldForUser) &&
              !isAdmin
            ) {
              return false;
            }

            return true;
          })
          .map((ticketType) => {
            const selectedQuantity = selectedTickets?.find(
              (seat) => seat.id === ticketType.id && seat.zoneId === zone?.name
            );

            return (
              <ListItem
                key={`${zone?.name}-${ticketType.id}`}
                ticketType={ticketType}
                showHiddenFees={showHiddenFees}
                handleChange={(
                  id: string,
                  quantity: number,
                  type: TicketTypeType
                ) => onChange(id, quantity, type, zone?.name, ticketType.price)}
                quantity={selectedQuantity?.quantity ?? 0}
              />
            );
          });

        if (zone.description || ticketListItems.length > 0) {
          return (
            <Box mb={4} key={zone?.name}>
              <Text
                color="N800"
                fontWeight="heavy"
                fontSize={{ _: 6, md: 7 }}
                textAlign={{ _: "left", md: "left" }}
                lineHeight="normal"
              >
                {zone?.name}
              </Text>
              {zone.description && (
                <Box
                  w="100%"
                  p={2}
                  bg="N100"
                  borderRadius="sm"
                  border="1px"
                  borderColor="N300"
                  mt={2}
                >
                  <Text
                    color="N800"
                    fontSize={3}
                    textAlign={{ _: "left", md: "left" }}
                    lineHeight="medium"
                  >
                    {zone.description}
                  </Text>
                </Box>
              )}

              <>
                <Divider my={{ _: 2, md: 2 }} />

                {ticketListItems}

                {hasFeature("ticketResale") &&
                  hasResaleWaitlist &&
                  !isResaleRelease &&
                  (() => {
                    const soldOutZone = soldOutZones?.find(
                      (z) => z.id === zone.id
                    );

                    const preventWaitlistJoining =
                      (requiresAuth && releaseType === ReleaseType.Unlisted) ||
                      releaseType === ReleaseType.Pos ||
                      releaseType === ReleaseType.Outlet;

                    if (!soldOutZone || preventWaitlistJoining) return null;

                    return (
                      <>
                        <Divider my={{ _: 2, md: 2 }} />
                        <SoldOutWaitlistMessage zoneName={soldOutZone.name} />
                      </>
                    );
                  })()}
              </>
            </Box>
          );
        }
      })}

      {addonTypes.length > 0 && (
        <>
          <Text
            color="N800"
            fontWeight="heavy"
            fontSize={{ _: 6, md: 7 }}
            textAlign={{ _: "center", md: "center" }}
            lineHeight={{ _: "normal", md: "high" }}
            mt={4}
            mb={1}
          >
            Select Addons
          </Text>
          <Divider mt={{ _: 2, md: 0 }} mb={{ _: 2, md: 3 }} />

          {addonTypes.map((addonType) => {
            const selectedQuantity = selectedAddons?.find(
              (addon) => addon.id === addonType.id && addon.zoneId === "no-zone"
            );

            return (
              <ListItem
                key={addonType.id}
                ticketType={addonType}
                handleChange={(
                  id: string,
                  quantity: number,
                  type: TicketTypeType
                ) => onChange(id, quantity, type, "no-zone", addonType.price)}
                quantity={selectedQuantity?.quantity ?? 0}
              />
            );
          })}
        </>
      )}
      <Divider mt={{ _: 3, md: 0 }} />
      {multibuyDiscount > 0 && (
        <>
          <Flex mt={2} justifyContent="space-between" w="100%">
            <Text fontWeight="medium" fontSize={2} color="N600">
              Sub-Total
            </Text>
            <Price
              price={lineItemsTotal}
              fontWeight="medium"
              fontSize={4}
              lineHeight={"18px" as any}
              color={"black" as any}
            />
          </Flex>
          <Flex mt={2} justifyContent="space-between" w="100%">
            <Text fontWeight="medium" fontSize={2} color="N600">
              Multibuy promotion applied
            </Text>
            <Price
              price={-multibuyDiscount}
              fontWeight="medium"
              fontSize={4}
              lineHeight={"18px" as any}
              color={"black" as any}
            />
          </Flex>
          <Divider my={{ _: 3, md: 2 }} />
        </>
      )}

      {activeReferralCampaign &&
        activeReferralCampaign.referralUserRewardType ===
          ReferralRewardType.Discount && (
          <>
            <Flex mt={2} justifyContent="space-between" w="100%">
              <Text fontWeight="medium" fontSize={2} color="N600">
                Sub-Total
              </Text>
              <Price
                price={lineItemsTotal}
                fontWeight="medium"
                fontSize={4}
                lineHeight={"18px" as any}
                color={"black" as any}
              />
            </Flex>
            <Flex mt={2} justifyContent="space-between" w="100%">
              <Text fontWeight="medium" fontSize={2} color="N600">
                Referral discount applied
              </Text>
              <Price
                price={-referralDiscount}
                fontWeight="medium"
                fontSize={4}
                lineHeight={"18px" as any}
                color={"black" as any}
              />
            </Flex>
            <Divider my={{ _: 3, md: 2 }} />
          </>
        )}

      <Flex mt={2} justifyContent="space-between" w="100%">
        <Text fontWeight="medium" fontSize={2} color="N600">
          Total {showHiddenFees ? "(including booking fees and taxes)" : ""}
        </Text>
        <Text
          fontWeight="medium"
          fontSize={4}
          lineHeight={"18px" as any}
          color={"black" as any}
        >
          {hasTicketSelected ? <Price price={totalPrice} /> : "-"}
        </Text>
      </Flex>

      {userCredits > 0 && hasTicketSelected && (
        <>
          <Flex mt={2} justifyContent="space-between" w="100%">
            <Text fontWeight="medium" fontSize={2} color="N600">
              Your credits to be deducted
            </Text>
            <Text
              fontWeight="medium"
              fontSize={4}
              lineHeight={"18px" as any}
              color={"black" as any}
            >
              {`- $${user.credits.toFixed(2)}`}
            </Text>
          </Flex>
          <Flex mt={2} justifyContent="space-between" w="100%">
            <Text fontWeight="medium" fontSize={2} color="N600">
              Total left to pay after deducting credits
            </Text>
            <Text
              fontWeight="medium"
              fontSize={4}
              lineHeight={"18px" as any}
              color={"black" as any}
            >
              {totalPrice - userCredits > 0 ? (
                <Price price={totalPrice - userCredits} />
              ) : (
                "$0.00"
              )}
            </Text>
          </Flex>
        </>
      )}

      {!isPOS && totalPrice - userCredits > 0 && (
        <>
          {bnplGateways.length > 0 &&
            bnplGateways.map((g) => (
              <Box mt={2} w="100%" key={g.id}>
                <BuyNowPayLater
                  totalPrice={totalPrice - userCredits}
                  hasTicketSelected={hasTicketSelected}
                  showHiddenFees={showHiddenFees}
                  isSmallDisplay={isMobile}
                  bnplIntegration={g as BNPLIntegration} // This was checked when filtering.
                />
              </Box>
            ))}
          {stripeAfterpayGateway && (
            <Flex flexDirection={"row-reverse"}>
              {/* @ts-expect-error - Afterpay placement component */}
              <afterpay-placement
                data-locale={getAfterPayLocaleByCurrency(organization.currency)}
                data-currency={organization.currency ?? "NZD"}
                data-amount={totalPrice - userCredits}
              >
                {/* @ts-expect-error - Afterpay placement component */}
              </afterpay-placement>
            </Flex>
          )}
        </>
      )}

      <PrimaryButton
        mt={{ _: 2, md: 6 }}
        padding="mobileGutter"
        w="100%"
        onClick={() => {
          void handleOrderCreation();
        }}
        isLoading={isSubmitting}
        disabled={hasTicketSelected === false}
      >
        {organization.id === "2648ddb4-b50c-489f-a3eb-ae0249e3db3c"
          ? "Join now"
          : actionButton()}
      </PrimaryButton>
      {/* DON"T REMOVE: 
       When youre on safari the buy tickets button is hidden as the nav bar is at the bottom. 
       We might be able to add bottom padding or use dvh css property */}
      <br />
      <br />
      <br />
      <br />
      <br />

      <ContentModal
        isOpen={!!alertError}
        setIsOpen={() => setAlertError(undefined)}
        title={alertError?.title}
      >
        {typeof alertError?.message === "string" ? (
          <div dangerouslySetInnerHTML={{ __html: alertError?.message }} />
        ) : (
          alertError?.message
        )}
      </ContentModal>
    </Box>
  );
};
